import {
  createContext,
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { usePostHog } from "posthog-js/react";
import type { AuthContextValue, AuthStatus, User } from "../types";
import { authService } from "../services/authService";

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<User | null>(null);
  const posthog = usePostHog();

  // Restore session on mount
  useEffect(() => {
    const restoreSession = async () => {
      try {
        const restoredUser = await authService.restoreSession();
        if (restoredUser) {
          setUser(restoredUser);
          setStatus("authenticated");
          posthog.identify(restoredUser.id, { email: restoredUser.email });
        } else {
          setStatus("unauthenticated");
        }
      } catch {
        setStatus("unauthenticated");
      }
    };

    restoreSession();
  }, [posthog]);

  const loginWithGoogle = useCallback(async (credential: string) => {
    setStatus("loading");
    try {
      const loggedInUser = await authService.loginWithGoogle(credential);
      setUser(loggedInUser);
      setStatus("authenticated");
      posthog.identify(loggedInUser.id, { email: loggedInUser.email });
    } catch (error) {
      setStatus("unauthenticated");
      throw error;
    }
  }, [posthog]);

  const logout = useCallback(async () => {
    await authService.logout();
    setUser(null);
    setStatus("unauthenticated");
    posthog.reset();
  }, [posthog]);

  const getAccessToken = useCallback(async () => {
    try {
      return await authService.getAccessToken();
    } catch {
      // Token refresh failed, user needs to re-authenticate
      setUser(null);
      setStatus("unauthenticated");
      throw new Error("Session expired");
    }
  }, []);

  const updateUser = useCallback((updates: Partial<User>) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : null));
  }, []);

  const value: AuthContextValue = {
    status,
    user,
    loginWithGoogle,
    logout,
    getAccessToken,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
